import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import Iconify from '../Iconify';
import { createDentallyAppointmentAsync } from '../../utils/dentallyApi';
import { callApi } from '../../utils/apiCall';
import { nextStepCard, nextStepIcon, nextStepTitle, nextStepDescription, nextStepCTA } from '../../styles/OverView';

const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'center' };

/**
 * Amber banner shown on the lead Overview tab when a deferred Dentally booking
 * is awaiting payment confirmation. Lets staff complete the Dentally booking
 * once payment has been confirmed out-of-band.
 *
 * Reads `dentally_pending_booking` from `leadDetail.lead_metadata` (JSON blob).
 * Returns null when no pending booking exists.
 */
export default function DentallyPendingBookingBanner({ practiceId, leadDetail, onComplete }) {
  const { enqueueSnackbar } = useSnackbar();
  const [completing, setCompleting] = useState(false);
  const [retryError, setRetryError] = useState(null);

  const metadata = Array.isArray(leadDetail?.lead_metadata) ? leadDetail.lead_metadata : [];
  const pendingMeta = metadata.find((m) => m.key === 'dentally_pending_booking');
  const pendingData = (() => {
    if (!pendingMeta?.value) return null;
    try { return JSON.parse(pendingMeta.value); } catch { return null; }
  })();

  const leadId = leadDetail?.id;

  const apptTimeLabel = (() => {
    if (!pendingData?.dateTime) return null;
    try {
      return new Date(pendingData.dateTime).toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
    } catch { return null; }
  })();

  const handleCompleteBooking = useCallback(async () => {
    if (!practiceId || !leadId || !pendingData) return;
    setCompleting(true);
    setRetryError(null);
    try {
      const { practitionerId, dateTime, durationMinutes, reason, method, assignees, notes, treatmentRecordId } = pendingData;
      const startIso = dateTime;
      const finishIso = new Date(new Date(startIso).getTime() + (durationMinutes || 30) * 60000).toISOString();

      // Step 1: Create CRM appointment (was skipped in deferred mode)
      const aRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/`, 'post', {
        datetime: startIso,
        method: method || 'IN_PRACTICE',
        type: 'CONSULTATION',
        contacted_via: 'PHONE',
        lead_treatment: treatmentRecordId,
        assignees: assignees || [],
      });
      if (!aRes.ok) {
        const msg = aRes.data?.detail || aRes.data?.message || 'Failed to create appointment';
        setRetryError(msg);
        return;
      }

      // Step 2: Create Dentally appointment (was skipped in deferred mode)
      const dRes = await createDentallyAppointmentAsync(practiceId, {
        lead_id: leadId,
        start_time: startIso,
        finish_time: finishIso,
        practitioner_id: practitionerId,
        reason: reason || 'Exam',
        ...(notes ? { notes } : {}),
      });

      const dentallyWarning = dRes?.ok ? null
        : (dRes?.data?.detail || dRes?.data?.message || 'Dentally sync failed — appointment saved in CRM only');

      // Step 3: Clear the pending booking entry from metadata
      const metaRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
      if (metaRes.ok) {
        const existing = Array.isArray(metaRes.data?.lead_metadata) ? metaRes.data.lead_metadata : [];
        const updated = existing.map((m) =>
          m.key === 'dentally_pending_booking' ? { ...m, value: '' } : m
        );
        await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', { lead_metadata: updated });
      }

      if (dentallyWarning) {
        enqueueSnackbar(`Appointment booked in CRM. Note: ${dentallyWarning}`, { variant: 'warning', anchorOrigin: SNACKBAR_ANCHOR });
      } else {
        enqueueSnackbar('Dentally booking completed successfully', { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR });
      }
      onComplete?.();
    } catch (err) {
      setRetryError(err?.message || 'An unexpected error occurred');
    } finally {
      setCompleting(false);
    }
  }, [practiceId, leadId, pendingData, enqueueSnackbar, onComplete]);

  if (!pendingData) return null;

  const AMBER = '#FFAB00';

  return (
    <Box sx={{ ...nextStepCard('rgba(255,171,0,0.08)'), mt: 2 }}>
      <Box sx={nextStepIcon(AMBER)}>
        <Iconify icon="mdi:cash-clock" width={24} height={24} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={nextStepTitle}>Payment Pending — Complete Dentally Booking</Typography>
        <Typography sx={nextStepDescription}>
          {apptTimeLabel ? `Slot: ${apptTimeLabel}` : 'Appointment slot saved'}
          {pendingData.treatmentName ? ` · ${pendingData.treatmentName}` : ''}
        </Typography>
        <Typography sx={{ ...nextStepDescription, mt: 0.25 }}>
          Once payment is confirmed, click to book the appointment in Dentally.
        </Typography>
        {retryError && (
          <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
            {retryError}
          </Typography>
        )}
      </Box>
      <LoadingButton
        variant="contained"
        loading={completing}
        onClick={handleCompleteBooking}
        startIcon={<Iconify icon="mdi:calendar-check" width={16} />}
        sx={{ ...nextStepCTA, bgcolor: AMBER, '&:hover': { bgcolor: AMBER, opacity: 0.9 }, flexShrink: 0 }}
      >
        Complete Dentally Booking
      </LoadingButton>
    </Box>
  );
}

DentallyPendingBookingBanner.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadDetail: PropTypes.object,
  onComplete: PropTypes.func,
};

DentallyPendingBookingBanner.defaultProps = {
  leadDetail: null,
  onComplete: null,
};
